/** Batch "Export XML/JSON" dialog — shared by the Activities / Itineraries /
 *  Tours / Users / Bookings grids (a custom `dialog(ctx)` batch action).
 *
 *  Unlike the other batch dialogs this does NOT go through the generic
 *  `/batch/` dispatch (`ctx.runStandard`): it POSTs to the grid's own
 *  `/<resource>/document_export/` endpoint, forwarding the grid's list query
 *  string so an all-matching selection resolves to exactly the rows on
 *  screen. The export runs as a background task; the finished file appears
 *  as a Background Task artifact (rendered by the existing bell / task
 *  details dialog).
 *
 *  Content "hints" come from the shared `/entity_exports/contract/` lookup
 *  (fetched once, cached forever) — every hint is included by default and an
 *  unchecked box goes into `hints.exclude`.
 */
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  FormLabel,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';

import { ConsoleApiError } from '@/api/client';
import {
  fetchEntityExportContract,
  runDocumentExport,
  type DocumentExportBody,
  type EntityExportFormat,
} from '@/api/entityExports';
import { searchImportSources } from '@/api/integrations';
import { AppDialog } from '@/components/AppDialog';
import type { BatchActionContext, BatchActionDef } from '@/components/DataGrid/batchTypes';
import { useSnackbar } from '@/components/Snackbar';
import { openBackgroundTask } from '@/features/bgtasks/openBackgroundTask';
import { watchExportTask } from '@/features/exports/watchExportTask';

/** Import_Source.code is a short code string — keep pathological input out. */
const MAX_SOURCE_LENGTH = 64;

/** The "Export XML/JSON…" batch action, one per exporting grid — mirrors
 *  `deleteBatchAction` so all five grids wire it identically. Non-destructive;
 *  gated on the grid's own `console_<resource>_export` codename. */
export function entityExportBatchAction(cfg: {
  /** Console permission gating the menu item (backend re-checks). */
  permission: string;
  /** Contract key: activity / itinerary / tour / booking / user. */
  entity: string;
  /** URL segment of the per-grid endpoint (`/<resource>/document_export/`). */
  resource: string;
  /** Count-friendly noun for dialog copy, e.g. "booking(s)". */
  noun: string;
}): BatchActionDef {
  return {
    key: 'export',
    label: 'Export XML/JSON…',
    icon: <FileDownloadIcon fontSize="small" />,
    permission: cfg.permission,
    dialog: (ctx) => (
      <EntityExportDialog ctx={ctx} entity={cfg.entity} resource={cfg.resource} noun={cfg.noun} />
    ),
  };
}

interface Props {
  ctx: BatchActionContext;
  /** Contract key for this grid's rows: activity / itinerary / tour / booking / user. */
  entity: string;
  /** URL segment of the per-grid endpoint (`/<resource>/document_export/`). */
  resource: string;
  /** Count-friendly noun for dialog copy, e.g. "booking(s)". */
  noun: string;
}

export function EntityExportDialog({ ctx, entity, resource, noun }: Props) {
  const snackbar = useSnackbar();
  const [format, setFormat] = useState<EntityExportFormat>('xml');
  // Hint keys the user UNchecked — empty set = everything included (default).
  const [excluded, setExcluded] = useState<Set<string>>(new Set());
  const [sourceCode, setSourceCode] = useState('');
  // Multi-tenant delivery: one combined document, or one file per operator.
  const [tenantFiles, setTenantFiles] = useState<'combined' | 'per_tenant'>('combined');

  // One contract for all five entities — fetched once and cached (immutable
  // per deploy; a stale entry only mislabels a checkbox, never breaks a POST).
  const contractQuery = useQuery({
    queryKey: ['entityExportContract'],
    queryFn: fetchEntityExportContract,
    staleTime: Infinity,
  });
  const hints = contractQuery.data?.[entity]?.hints ?? [];

  // Import source codes are per-operator, so a source picker only makes sense
  // when the whole export provably belongs to ONE tenant:
  //  1. an active tenant filter pins every matched row to that tenant;
  //  2. else, an ids selection whose rows are all on the loaded page (a null
  //     entry means "row not loaded / tenant unknown") and share one tenant.
  // Anything else (all-matching without a tenant filter, a selection that
  // crossed pages, mixed tenants) exports without a source instead.
  const singleTenantId = useMemo(() => {
    if (ctx.tenantFilterId != null) return ctx.tenantFilterId;
    if (ctx.selection.mode !== 'ids') return null;
    const tenants = ctx.rowTenantIds;
    const first = tenants[0];
    if (first == null) return null;
    return tenants.every((t) => t === first) ? first : null;
  }, [ctx.tenantFilterId, ctx.selection.mode, ctx.rowTenantIds]);

  const sourcesQuery = useQuery({
    queryKey: ['entityExportSources', singleTenantId],
    queryFn: () => searchImportSources(singleTenantId!, ''),
    enabled: singleTenantId != null,
    staleTime: 60_000,
    // A 403 (no console_restech_view) is a stable answer, not a blip — fall
    // straight through to the manual code field.
    retry: false,
  });

  const trimmedSource = sourceCode.trim();
  const sourceTooLong = trimmedSource.length > MAX_SOURCE_LENGTH;

  const toggleHint = (key: string) => {
    setExcluded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const mutation = useMutation({
    mutationFn: () => {
      // Only known hint keys go on the wire (a stale unchecked key from a
      // contract refetch must not 400 the request).
      const exclude = hints.filter((h) => excluded.has(h.key)).map((h) => h.key);
      const body: DocumentExportBody = {
        format,
        selection: ctx.selection,
        hints: { exclude },
        // Single-tenant: the picked (or typed) source code. Multi-tenant: the
        // delivery mode instead — the backend stamps each operator's default
        // import key (the two are mutually exclusive on the wire).
        ...(singleTenantId != null
          ? trimmedSource
            ? { source_id: trimmedSource }
            : {}
          : { tenant_files: tenantFiles }),
      };
      return runDocumentExport(resource, body, ctx.filterQuery);
    },
    onSuccess: (res) => {
      if (res === null) {
        // 204 — the selection resolved to nothing; keep the selection so the
        // operator can see what they picked.
        snackbar.show('Nothing to export.', 'info');
        ctx.close();
        return;
      }
      snackbar.show('Export started — follow progress in Background Tasks.', 'success', {
        action: { label: 'View task', onClick: () => openBackgroundTask(res.task_id) },
      });
      // Detached watcher: offers the artifact in a snackbar when the task
      // finishes (survives this dialog closing).
      watchExportTask(res.task_id, snackbar);
      ctx.clearSelection();
      ctx.close();
    },
    onError: (e) =>
      snackbar.show(e instanceof ConsoleApiError ? e.message : 'Export failed.', 'error'),
  });

  const submit = () => {
    if (sourceTooLong || mutation.isPending) return;
    mutation.mutate();
  };

  return (
    <AppDialog
      open
      onClose={ctx.close}
      title="Export XML/JSON"
      size="sm"
      actions={
        <>
          <Button onClick={ctx.close}>Cancel</Button>
          <Button
            variant="contained"
            disabled={sourceTooLong || mutation.isPending}
            onClick={submit}
          >
            Export {ctx.count.toLocaleString()} {noun}
          </Button>
        </>
      }
    >
      <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Export the selected {noun} as a document. Runs in the background — the finished
          file appears as an artifact on the export&apos;s Background Task.
        </Typography>

        <Box>
          <FormLabel id="entity-export-format-label" sx={{ typography: 'body2' }}>
            Format
          </FormLabel>
          <RadioGroup
            row
            aria-labelledby="entity-export-format-label"
            value={format}
            onChange={(e) => setFormat(e.target.value as EntityExportFormat)}
          >
            <FormControlLabel value="xml" control={<Radio size="small" />} label="XML" />
            <FormControlLabel value="json" control={<Radio size="small" />} label="JSON" />
          </RadioGroup>
        </Box>

        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
            Include in the document (unchecked blocks are left out):
          </Typography>
          {contractQuery.isPending ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 1 }}>
              <CircularProgress size={20} />
            </Box>
          ) : contractQuery.isError ? (
            <Alert severity="warning">
              Couldn&apos;t load the export options — the export will include all content.
            </Alert>
          ) : (
            <FormGroup>
              {hints.map((h) => (
                <Tooltip key={h.key} title={h.description} placement="right" describeChild>
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={!excluded.has(h.key)}
                        onChange={() => toggleHint(h.key)}
                      />
                    }
                    label={h.label}
                  />
                </Tooltip>
              ))}
            </FormGroup>
          )}
        </Box>

        {singleTenantId == null ? (
          // Multi-tenant (or tenant-unknown) export: pick the delivery shape;
          // sources are resolved per operator by the backend.
          <Box>
            <FormLabel id="entity-export-tenant-files-label" sx={{ typography: 'body2' }}>
              Multiple operators — file layout
            </FormLabel>
            <RadioGroup
              aria-labelledby="entity-export-tenant-files-label"
              value={tenantFiles}
              onChange={(e) => setTenantFiles(e.target.value as 'combined' | 'per_tenant')}
            >
              <FormControlLabel
                value="combined"
                control={<Radio size="small" />}
                label="One combined file with all operators"
              />
              <FormControlLabel
                value="per_tenant"
                control={<Radio size="small" />}
                label="One file per operator"
              />
            </RadioGroup>
            <Alert severity="info" sx={{ mt: 1 }}>
              The selection may span more than one tour operator (no tenant filter is
              active, and the selected rows aren&apos;t all on this page under a single
              operator). Import source codes are per-operator, so each record is stamped
              with its operator&apos;s <em>default import key</em> and the file re-imports
              under the right source automatically. Operators missing a default import key
              are skipped — the export logs an error for each in its Background Task. To
              re-import under a different source, edit the exported file&apos;s{' '}
              <code>source_id</code> values manually.
            </Alert>
          </Box>
        ) : sourcesQuery.isPending ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 1 }}>
            <CircularProgress size={20} />
          </Box>
        ) : sourcesQuery.isError ? (
          // Can't list this operator's sources (needs console_restech_view) —
          // fall back to entering the code by hand.
          <TextField
            size="small"
            label="Import source code (optional)"
            value={sourceCode}
            onChange={(e) => setSourceCode(e.target.value)}
            error={sourceTooLong}
            helperText={
              sourceTooLong
                ? `Max ${MAX_SOURCE_LENGTH} characters.`
                : "Couldn't list this operator's import sources — enter the code manually. " +
                  'It stamps a <sources> block so the file re-imports under that source.'
            }
          />
        ) : sourcesQuery.data.length === 0 ? null : (
          <FormControl size="small">
            <InputLabel id="entity-export-source-label">Import source (optional)</InputLabel>
            <Select
              labelId="entity-export-source-label"
              label="Import source (optional)"
              value={sourceCode}
              onChange={(e) => setSourceCode(e.target.value)}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {sourcesQuery.data.map((s) => (
                <MenuItem key={s.code} value={s.code}>
                  {s.name}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>
              Stamps a &lt;sources&gt; block so the file can be re-imported under that
              source.
            </FormHelperText>
          </FormControl>
        )}

        {ctx.selection.mode === 'all_matching' && (
          <Alert severity="info">Exporting {ctx.matchedPhrase} {noun}.</Alert>
        )}
      </Box>
    </AppDialog>
  );
}
